import { cloneDeep } from 'lodash-es';
import debounce from 'lodash/debounce';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import styled from 'styled-components';
import { ampli } from '../../../../../../ampli';
import { IDirectDealFilter } from '../../../../../../constants/filter/directDealFilter.constant';
import {
  DirectDealSliderFilterKeyType,
  IDirectDealSliderQuickSetting,
  directDealFilterSliderConstant,
  directDealSliderQuickSettings,
  isDirectDealSliderFilterKey,
} from '../../../../../../constants/filter/slider/directDealFilterSliderConstant';
import { useGetOpenMarketProductsMetaQuery } from '../../../../../../hooks/api/product/queries/useGetOpenMarketProductsMeta.query';
import { useAppDispatch, useAppSelector } from '../../../../../../hooks/store/useStore';
import { FilterOptionValueType, IFilter, IFilterOption } from '../../../../../../interfaces/Filter.interfaces';
import { filterSlice } from '../../../../../../store/filterSlice';
import { changeFilterOptionIsSelected } from '../../../../../../utils/filter/changeFilterOptionIsSelected.util';
import { changeFilterOptionValue } from '../../../../../../utils/filter/changeFilterOptionValue.util';
import { createDirectDealFilterSearchParams } from '../../../../../../utils/filter/createDirectDealFilterSearchParams.util';
import {
  formatDirectDealSliderRangeText,
  getDirectDealSliderIndex,
} from '../../../../../../utils/filter/directDealSliderValue.util';
import { isFilterSelectedAsAll } from '../../../../../../utils/filter/isFilterSelectedAsAll.util';
import { resetFilterOptionIsSelected } from '../../../../../../utils/filter/resetFilterOptionIsSelected.util';
import { ToggleSwitch } from '../../../../inputs/ToggleSwitch';
import { SpinnerLoader } from '../../../../loading/SpinnerLoader';
import { FilterSlider } from '../FilterController/components/FilterSlider';

const FILTER_TYPE = 'directDealFilter';

// 바텀시트에 보여 줄 항목과 순서. 브랜드와 정렬은 리스트 화면에서 따로 다룬다.
const FILTER_KEYS_IN_ORDER = [
  'machineType',
  'engineOutputInPs',
  'rowConfig',
  'price',
  'manufacturedYear',
  'region',
  'operationTime',
  'status',
];

interface DirectDealFilterControllerProps {
  closeModal: () => void;
}

export const DirectDealFilterController = ({ closeModal }: DirectDealFilterControllerProps) => {
  const dispatch = useAppDispatch();
  const filterStates = useAppSelector((state) => state.filter[FILTER_TYPE]);
  const filterActions = filterSlice.actions;

  // 전역 상태로 적용된 필터 값을 지역 상태로 가져와 사용하고, 'n대 보기'를 눌렀을 때만 전역에 반영한다.
  const [localFilters, setLocalFilters] = useState<IFilter[]>(cloneDeep(filterStates.globalFilters));
  const [isFilterLoading, setIsFilterLoading] = useState(false);
  // 슬라이더를 움직이는 동안의 값. 지역 상태에는 잠시 뒤에 반영되므로, 요약 문구는 이 값을 먼저 보여 준다.
  const [draggingSliderValues, setDraggingSliderValues] = useState<Record<string, number[]>>({});

  const filterRefs = useRef<Record<string, HTMLDivElement | null>>({});
  const filterSelectSectionRef = useRef<HTMLDivElement | null>(null);

  const debouncedUpdateFilters = useCallback(
    debounce((newFilters: IFilter[]) => {
      setLocalFilters(newFilters);
      setIsFilterLoading(false);
    }, 500),
    [],
  );

  useEffect(() => {
    if (filterStates.haveToRefreshLocalStates) {
      setLocalFilters(cloneDeep(filterStates.globalFilters));
      dispatch(filterActions.changeHaveToRefreshLocalStates({ filterType: FILTER_TYPE, value: false }));
    }
  }, [filterStates.haveToRefreshLocalStates, filterStates.globalFilters, dispatch, filterActions]);

  // 알약 필터를 눌러 열었을 때 해당 항목으로 이동한다.
  useEffect(() => {
    if (typeof filterStates.clickedFilterButtonIndex === 'number') {
      const clickedFilterKey = filterStates.globalFilters[filterStates.clickedFilterButtonIndex]?.key;

      // 슬라이드 모달이 올라온 뒤에야 offsetTop이 제대로 잡혀서 잠시 기다린다.
      setTimeout(() => {
        // 첫 항목은 그 위의 '최근 7일' 토글까지 보이도록 맨 위로 이동한다.
        const scrollTop =
          clickedFilterKey === FILTER_KEYS_IN_ORDER[0] ? 0 : filterRefs.current[clickedFilterKey]?.offsetTop ?? 0;

        filterSelectSectionRef.current?.scrollTo({ top: scrollTop });
      }, 200);

      dispatch(filterActions.changeClickedFilterButtonIndex({ filterType: FILTER_TYPE, value: undefined }));
    }
  }, [filterStates.clickedFilterButtonIndex, filterStates.globalFilters, dispatch, filterActions]);

  useEffect(() => {
    setDraggingSliderValues({});
  }, [localFilters]);

  const { data: productsMeta, isLoading: isGetProductsMetaQueryLoading } = useGetOpenMarketProductsMetaQuery({
    searchParam: createDirectDealFilterSearchParams({ filters: localFilters as IDirectDealFilter[] }),
  });

  const getLocalFilter = (filterKey: string) => localFilters.find((filter) => filter.key === filterKey);

  const selectedMachineTypes = useMemo<FilterOptionValueType[]>(() => {
    return (
      localFilters
        .find((filter) => filter.key === 'machineType')
        ?.options.filter((option) => option.isSelected)
        .map((option) => option.value) ?? []
    );
  }, [localFilters]);

  // 농기계를 선택하지 않으면 전체가 조회 대상이다.
  const isMachineTypeTargeted = (machineTypes: FilterOptionValueType[], machineType: string) =>
    machineTypes.length === 0 || machineTypes.includes(machineType);

  const getHiddenFilterKeys = (machineTypes: FilterOptionValueType[]): string[] => {
    const hiddenFilterKeys: string[] = [];

    // 마력은 트랙터에만, 조식은 콤바인·이앙기에만 있는 값이다.
    if (!isMachineTypeTargeted(machineTypes, 'tractor')) {
      hiddenFilterKeys.push('engineOutputInPs');
    }

    if (!isMachineTypeTargeted(machineTypes, 'combine') && !isMachineTypeTargeted(machineTypes, 'transplanter')) {
      hiddenFilterKeys.push('rowConfig');
    }

    return hiddenFilterKeys;
  };

  const hiddenFilterKeys = getHiddenFilterKeys(selectedMachineTypes);

  const handleFilterOptionClick = (filter: IFilter, option: IFilterOption) => {
    let newFilters = changeFilterOptionIsSelected({
      currentFilters: localFilters,
      targetFilter: filter,
      targetOption: option,
    });

    // 숨겨지는 항목과 다시 보이는 항목은 값을 초기화해, 이전 설정이 남아 있지 않게 한다.
    if (filter.key === 'machineType') {
      const newSelectedMachineTypes =
        newFilters
          .find((newFilter) => newFilter.key === 'machineType')
          ?.options.filter((newOption) => newOption.isSelected)
          .map((newOption) => newOption.value) ?? [];

      [...hiddenFilterKeys, ...getHiddenFilterKeys(newSelectedMachineTypes)].forEach((hiddenFilterKey) => {
        const hiddenFilter = newFilters.find((newFilter) => newFilter.key === hiddenFilterKey);

        if (hiddenFilter) {
          newFilters = resetFilterOptionIsSelected({ currentFilters: newFilters, targetFilter: hiddenFilter });
        }
      });
    }

    setLocalFilters(newFilters);
  };

  const handleFilterSliderChange = (filter: IFilter, optionValues: FilterOptionValueType[]) => {
    setIsFilterLoading(true);
    setDraggingSliderValues((prev) => ({ ...prev, [filter.key]: optionValues.map(Number) }));

    const newFilters = changeFilterOptionValue({
      currentFilters: localFilters,
      targetFilter: filter,
      newOptionValues: optionValues,
    });

    debouncedUpdateFilters(newFilters);
  };

  // 퀵 세팅은 선택 상태를 갖지 않고, 누를 때마다 슬라이더 범위를 해당 범위로 바꾼다.
  const handleQuickSettingClick = (filter: IFilter, quickSetting: IDirectDealSliderQuickSetting) => {
    const filterKey = filter.key as DirectDealSliderFilterKeyType;
    const sliderConstant = directDealFilterSliderConstant[filterKey];

    const minSliderIndex =
      quickSetting.min !== undefined ? getDirectDealSliderIndex(filterKey, quickSetting.min) : undefined;
    const maxSliderIndex =
      quickSetting.max !== undefined ? getDirectDealSliderIndex(filterKey, quickSetting.max) : undefined;

    // 슬라이더를 움직이던 중이면 그 변경이 뒤늦게 덮어쓰지 않도록 취소한다.
    debouncedUpdateFilters.cancel();
    setIsFilterLoading(false);

    setLocalFilters(
      changeFilterOptionValue({
        currentFilters: localFilters,
        targetFilter: filter,
        newOptionValues: [minSliderIndex ?? sliderConstant.min, maxSliderIndex ?? sliderConstant.max],
      }),
    );
  };

  const handleResetButtonClick = () => {
    ampli.pvmClickFilterControllerResetButton({ filter_type: FILTER_TYPE, filters: [...localFilters] });

    setLocalFilters(resetFilterOptionIsSelected({ currentFilters: localFilters }));
  };

  const hasAnyActiveFilter = useMemo<boolean>(() => {
    return localFilters.some((filter) => {
      if (filter.optionType === 'slider') {
        return filter.options.some((option) => option.value !== option.defaultValue);
      }

      return filter.options.some((option) => option.isSelected !== Boolean(option.isDefault));
    });
  }, [localFilters]);

  const handleApplyButtonClick = () => {
    ampli.pvmClickFilterControllerSubmitButton({
      filter_type: FILTER_TYPE,
      is_any_filter_selected: hasAnyActiveFilter,
      filters: [...localFilters],
    });

    dispatch(filterActions.changeFilters({ filterType: FILTER_TYPE, filters: localFilters }));
    dispatch(filterActions.changeHaveToRefreshLocalStates({ filterType: FILTER_TYPE, value: true }));
    window.scrollTo(0, 0);
    closeModal();
  };

  // 항목 우측에 현재 선택을 요약해 보여 준다. (예: 전체 / 콤바인 / 콤바인 외 1개)
  const getSelectedOptionSummary = (filter: IFilter): string => {
    const selectedOptions = filter.options.filter((option) => option.isSelected);

    if (selectedOptions.length === 0 || isFilterSelectedAsAll(filter)) {
      return '전체';
    }

    if (selectedOptions.length === 1) {
      return selectedOptions[0].optionName;
    }

    return `${selectedOptions[0].optionName} 외 ${selectedOptions.length - 1}개`;
  };

  const getSliderRangeSummary = (filter: IFilter): string => {
    const sliderValues = draggingSliderValues[filter.key] ?? filter.options.map((option) => Number(option.value));

    return formatDirectDealSliderRangeText(filter.key as DirectDealSliderFilterKeyType, sliderValues);
  };

  const recentDaysFilter = getLocalFilter('recentDays');
  const recentDaysOption = recentDaysFilter?.options[0];

  const hasNoProduct = !productsMeta?.totalCount || productsMeta.totalCount <= 0;
  const isConfirmButtonLoading = isGetProductsMetaQueryLoading || isFilterLoading;

  return (
    <Container>
      <FilterSelectSection ref={filterSelectSectionRef}>
        {recentDaysFilter && recentDaysOption && (
          <RecentDaysWrap>
            <FilterTitle>{`${recentDaysOption.optionName} 등록 매물만 보기`}</FilterTitle>
            <ToggleSwitch
              isChecked={recentDaysOption.isSelected}
              onClick={() => handleFilterOptionClick(recentDaysFilter, recentDaysOption)}
            />
          </RecentDaysWrap>
        )}

        {FILTER_KEYS_IN_ORDER.map((filterKey) => {
          const filter = getLocalFilter(filterKey);

          if (!filter || hiddenFilterKeys.includes(filterKey)) {
            return null;
          }

          return (
            <FilterWrap
              key={filter.key}
              ref={(element) => {
                filterRefs.current[filter.key] = element;
              }}
            >
              <FilterTitleWrap>
                <FilterTitle>{filter.title}</FilterTitle>
                <FilterSummaryText>
                  {filter.optionType === 'slider' ? getSliderRangeSummary(filter) : getSelectedOptionSummary(filter)}
                </FilterSummaryText>
              </FilterTitleWrap>

              {filter.optionType !== 'slider' && (
                <OptionButtonGroup>
                  {filter.options.map((option) => (
                    <OptionButton
                      key={`${filter.key}-${option.value}`}
                      isSelected={option.isSelected}
                      onClick={() => handleFilterOptionClick(filter, option)}
                    >
                      {option.optionName}
                    </OptionButton>
                  ))}
                </OptionButtonGroup>
              )}

              {filter.optionType === 'slider' && isDirectDealSliderFilterKey(filter.key) && (
                <QuickSettingButtonGroup>
                  {directDealSliderQuickSettings[filter.key].map((quickSetting) => (
                    <QuickSettingButton
                      key={`${filter.key}-${quickSetting.label}`}
                      onClick={() => handleQuickSettingClick(filter, quickSetting)}
                    >
                      {quickSetting.label}
                    </QuickSettingButton>
                  ))}
                </QuickSettingButtonGroup>
              )}

              {filter.optionType === 'slider' && isDirectDealSliderFilterKey(filter.key) && (
                <SliderStyleWrap>
                  <FilterSlider
                    options={filter.options}
                    handleChange={(newOptionValues: FilterOptionValueType[]) =>
                      handleFilterSliderChange(filter, newOptionValues)
                    }
                    sliderConstant={directDealFilterSliderConstant[filter.key]}
                    isValuePreviewHidden
                  />
                </SliderStyleWrap>
              )}
            </FilterWrap>
          );
        })}
      </FilterSelectSection>

      <ButtonContainer>
        <BlurSection />

        <GreyButton onClick={handleResetButtonClick}>
          <GreyButtonText>{'초기화'}</GreyButtonText>
        </GreyButton>
        <ConfirmButton disabled={hasNoProduct || isConfirmButtonLoading} onClick={handleApplyButtonClick}>
          {!isConfirmButtonLoading && (
            <ConfirmButtonText>{hasNoProduct ? '매물 없음' : `${productsMeta?.totalCount}대 보기`}</ConfirmButtonText>
          )}
          <SpinnerLoader isVisible={isConfirmButtonLoading} />
        </ConfirmButton>
      </ButtonContainer>
    </Container>
  );
};

const Container = styled.div`
  height: 700px;

  display: flex;
  align-items: flex-start;
  align-self: stretch;

  background: var(--Light-Scheme-Monochrome-white, #fff);

  position: relative;
`;

const FilterSelectSection = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  flex: 1 0 0;
  align-self: stretch;

  position: relative;

  /* 하단 버튼(72px)·그라데이션(40px)에 마지막 항목이 가리지 않도록, 피그마처럼 그 위에 149px를 더 비운다. */
  padding-bottom: 261px;

  overflow-y: scroll;

  -ms-overflow-style: none; /* IE and Edge */
  scrollbar-width: none; /* Firefox */
  ::-webkit-scrollbar {
    display: none; /* Chrome, Safari, Opera*/
  }
`;

const RecentDaysWrap = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  align-self: stretch;
  gap: 8px;

  /* 시안의 구분선은 안쪽 선이라 높이를 차지하지 않으므로, 아래 여백에서 선 두께만큼 뺀다. */
  padding: 16px 24px 23px 24px;

  border-bottom: 1px solid var(--Light-Scheme-Monochrome-grey20, #ececf3);

  /* 공용 토글(antd Switch)을 다른 화면과 따로, 시안 크기(44x24, 4px 안쪽에 16px 손잡이)에 맞춘다. */
  & > div {
    padding: 0px;
  }

  .ant-switch {
    min-width: 44px;
    height: 24px;

    vertical-align: top;
  }

  .ant-switch .ant-switch-handle {
    top: 4px;
    inset-inline-start: 4px;

    width: 16px;
    height: 16px;

    &::before {
      border-radius: 100px;
      box-shadow: none;
    }
  }

  .ant-switch.ant-switch-checked .ant-switch-handle {
    inset-inline-start: calc(100% - 20px);
  }
`;

const FilterWrap = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  align-self: stretch;

  position: relative;

  /* 시안의 구분선은 안쪽 선이라 높이를 차지하지 않으므로, 아래 여백에서 선 두께만큼 뺀다. */
  padding: 32px 0px 31px 0px;

  border-bottom: 1px solid var(--Light-Scheme-Monochrome-grey20, #ececf3);

  &:last-child {
    padding-bottom: 32px;

    border-bottom: none;
  }
`;

const FilterTitleWrap = styled.div`
  height: 36px;

  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  align-self: stretch;
  gap: 10px;

  padding: 0px 24px;
`;

const FilterTitle = styled.span`
  color: var(--Light-Scheme-Monochrome-black, #232429);

  /* Display/Large/l-bold */
  font-family: Pretendard;
  font-size: 16px;
  font-style: normal;
  font-weight: 700;
  line-height: 130%; /* 20.8px */
`;

const FilterSummaryText = styled(FilterTitle)`
  color: var(--Light-Scheme-Color-primary-blue, #196eff);
`;

const OptionButtonGroup = styled.div`
  width: 100%;

  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: 8px;

  padding: 0px 24px;
`;

const OptionButton = styled.button<{ isSelected: boolean }>`
  height: 40px;

  display: flex;
  justify-content: center;
  align-items: center;

  padding: 12px;

  border-radius: var(--radius-medium, 8px);
  border: 1px solid
    ${(props) =>
      props.isSelected
        ? 'var(--Light-Scheme-Color-primary-blue, #196eff)'
        : 'var(--Light-Scheme-Monochrome-grey30, #e0e0eb)'};
  background: ${(props) => (props.isSelected ? '#f0f5ff' : 'var(--Light-Scheme-Monochrome-white, #fff)')};

  color: ${(props) =>
    props.isSelected
      ? 'var(--Light-Scheme-Color-primary-blue, #196eff)'
      : 'var(--Light-Scheme-Monochrome-black, #232429)'};

  /* System/Large/l-medium */
  font-family: Pretendard;
  font-size: 16px;
  font-style: normal;
  font-weight: 500;
  line-height: 85%; /* 13.6px */
`;

const QuickSettingButtonGroup = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 8px;

  padding: 0px 24px 8px 24px;
`;

const QuickSettingButton = styled.button`
  height: 36px;

  display: flex;
  justify-content: center;
  align-items: center;

  padding: 12px;

  border-radius: 100px;
  border: 1px solid var(--Light-Scheme-Monochrome-grey30, #e0e0eb);
  background: var(--Light-Scheme-Monochrome-white, #fff);

  color: var(--Light-Scheme-Monochrome-black, #232429);

  /* System/Large/l-medium */
  font-family: Pretendard;
  font-size: 16px;
  font-style: normal;
  font-weight: 500;
  line-height: 85%; /* 13.6px */
`;

// 공용 슬라이더를 다른 필터와 따로, 직거래 시안(굵은 막대·흰 눈금·테두리 손잡이)에 맞춘다.
const SliderStyleWrap = styled.div`
  width: 100%;

  & > div {
    padding: 16px 32px;
  }

  /* MUI 기본 상하 여백(터치 기기는 20px)을 없애, 시안처럼 막대 위아래로 16px만 둔다. */
  .MuiSlider-root {
    height: 6px;
    padding: 0px;
  }

  .MuiSlider-rail {
    border-radius: 100px;
    background: var(--Light-Scheme-Monochrome-grey30, #e0e0eb);
    opacity: 1;
  }

  .MuiSlider-track {
    border: none;
    background: var(--Light-Scheme-Color-primary-blue, #196eff);
  }

  .MuiSlider-mark {
    width: 3px;
    height: 3px;

    border-radius: 100px;
    background: var(--Light-Scheme-Monochrome-white, #fff);
  }

  .MuiSlider-markActive {
    opacity: 0.5;
  }

  .MuiSlider-thumb {
    width: 24px;
    height: 24px;

    border: 8px solid var(--Light-Scheme-Color-primary-blue, #196eff);
    background: var(--Light-Scheme-Monochrome-white, #fff);
    box-shadow: 0px 2px 2px 0px rgba(35, 36, 41, 0.4);

    &::before {
      box-shadow: none;
    }

    &:hover,
    &.Mui-focusVisible,
    &.Mui-active {
      box-shadow: 0px 2px 2px 0px rgba(35, 36, 41, 0.4);
    }
  }
`;

const BlurSection = styled.div`
  position: absolute;
  left: 0px;
  top: -40px;

  width: 100%;
  height: 40px;

  background: linear-gradient(180deg, rgba(255, 255, 255, 0) 0%, #fff 63.96%);

  pointer-events: none;
`;

const ButtonContainer = styled.div`
  position: fixed;

  left: 0;
  bottom: 0;

  width: 100%;

  display: flex;
  align-items: center;
  align-self: stretch;

  padding: 0px 24px 32px 24px;
  gap: 8px;

  background-color: white;
`;

const GreyButton = styled.button`
  max-height: 40px;

  display: flex;
  justify-content: center;
  align-items: center;
  flex: 1 0 0;

  padding: 16px;
  gap: 4px;

  border-radius: var(--radius-medium, 8px);
  background: var(--Light-Scheme-Monochrome-grey20, #ececf3);
`;

const GreyButtonText = styled.span`
  color: var(--Light-Scheme-Monochrome-offblack, #505363);

  /* System/Medium/m-medium */
  font-family: Pretendard;
  font-size: 14px;
  font-style: normal;
  font-weight: 500;
  line-height: 85%; /* 11.9px */
`;

const ConfirmButton = styled.button`
  height: 40px;

  display: flex;
  justify-content: center;
  align-items: center;
  flex: 1 0 0;

  padding: 16px;
  gap: 4px;

  border-radius: var(--radius-medium, 8px);
  background: var(--Light-Scheme-Color-primary-blue, #196eff);

  &:disabled {
    opacity: 0.3;
  }
`;

const ConfirmButtonText = styled.span`
  color: var(--Light-Scheme-Monochrome-white, #fff);

  /* System/Medium/m-medium */
  font-family: Pretendard;
  font-size: 14px;
  font-style: normal;
  font-weight: 500;
  line-height: 85%; /* 11.9px */
`;
